import { apiGet, apiPost, apiDelete } from "./client";
import type {
  Company,
  CompanyInput,
  CompanySearchFilters,
  CompanySearchResponse,
  CompanySearchResult,
  MergedAccountView,
  SaveCompaniesRequest,
  SaveCompaniesResult,
  SavedCompanySearch,
  SavedCompanySearchInput,
} from "@sales-ops-amplify/shared";
import { getAccessToken } from "@/stores/authStore";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export function searchCompanies(
  filters: CompanySearchFilters,
): Promise<CompanySearchResponse> {
  return apiPost<CompanySearchResponse>("/api/companies/search", filters);
}

export function saveCompanies(
  body: SaveCompaniesRequest,
): Promise<SaveCompaniesResult> {
  return apiPost<SaveCompaniesResult>("/api/companies/save", body);
}

/** Immutable directory company detail (never overlays Account CRM fields). */
export function getDirectoryCompany(
  companyId: string,
): Promise<MergedAccountView> {
  return apiGet<MergedAccountView>(
    `/api/companies/${encodeURIComponent(companyId)}`,
  );
}

/** Export directory Company search results as CSV. */
export async function exportCompaniesCsv(
  companies: CompanySearchResult[],
): Promise<Blob> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/api/companies/export`, {
    method: "POST",
    headers,
    body: JSON.stringify({ companies }),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      typeof errBody === "object" && errBody && "error" in errBody
        ? String((errBody as { error: string }).error)
        : "Export failed",
    );
  }
  return res.blob();
}

export function getCompanyTechnologies(): Promise<{ technologies: string[] }> {
  return apiGet("/api/companies/technologies");
}

export function listSavedCompanySearches(): Promise<{
  searches: SavedCompanySearch[];
}> {
  return apiGet("/api/companies/saved-searches");
}

export function createSavedCompanySearch(
  input: SavedCompanySearchInput,
): Promise<SavedCompanySearch> {
  return apiPost("/api/companies/saved-searches", input);
}

export function deleteSavedCompanySearch(searchId: string): Promise<void> {
  return apiDelete(`/api/companies/saved-searches/${searchId}`);
}

export interface ListDirectoryCompaniesParams {
  search?: string;
  industry?: string;
  country?: string;
  limit?: number;
  offset?: number;
}

export interface ListDirectoryCompaniesResponse {
  companies: Company[];
  total: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  scope?: string;
  filters?: { industry?: string; country?: string; search?: string };
}

export interface CompanyFacets {
  industries: string[];
  countries: Array<{ slug: string; name: string; count: number }>;
}

/** List directory companies only. */
export async function listDirectoryCompanies(
  params?: ListDirectoryCompaniesParams,
): Promise<ListDirectoryCompaniesResponse> {
  return apiGet<ListDirectoryCompaniesResponse>("/api/companies", {
    scope: "directory",
    ...(params?.search ? { search: params.search } : {}),
    ...(params?.industry ? { industry: params.industry } : {}),
    ...(params?.country ? { country: params.country } : {}),
    ...(params?.limit != null ? { limit: params.limit } : {}),
    ...(params?.offset != null ? { offset: params.offset } : {}),
  });
}

export async function getCompanyFacets(): Promise<CompanyFacets> {
  return apiGet<CompanyFacets>("/api/companies/facets");
}

export async function createCompany(input: CompanyInput): Promise<Company> {
  const res = await apiPost<{ company: Company }>("/api/companies", input);
  return res.company;
}
